import type { Terminal } from '@xterm/xterm';

/**
 * Android GBoard compatibility shim for xterm's composition helper.
 *
 * xterm 6 derives terminal input from textarea diffs with
 * `newValue.replace(oldValue, "")`. That breaks whenever an Android IME
 * rewrites existing characters instead of appending: the old buffer is no
 * longer a contiguous substring of the new one, so xterm emits the entire
 * textarea contents (or a wrong span) to the PTY. Users see sentences appear
 * twice, interleaved fragments, and garbage after accepting a keyboard
 * suggestion ("tsuggesrikomtion"). Multi-character deletions are likewise
 * collapsed into a single DEL character.
 *
 * This module replaces the instance's diff computation with a terminal-safe
 * common-prefix diff. A PTY input stream cannot edit an arbitrary middle span:
 * its cursor is at the end of the text already sent. Therefore a rewrite
 * removes the complete old tail after the shared prefix and sends the complete
 * new tail. It hooks the same code path xterm uses (the keyCode 229 keydown
 * used by Android IMEs) so physical-key input, xterm paste handling, and
 * ordinary desktop browsers remain untouched.
 */

export type TerminalTextDiff = {
  /** Number of characters removed from the existing buffer (DEL count). */
  removedCount: number;
  /** Text inserted in place of the removed span. */
  insertedText: string;
};

/**
 * Computes a terminal-safe edit between two textarea buffers. Since DEL only
 * removes the character immediately before the PTY cursor, a changed suffix
 * cannot be retained in place: the entire old tail must be removed and the
 * entire new tail resent.
 */
export function terminalTextDiff(oldValue: string, newValue: string): TerminalTextDiff {
  if (oldValue === newValue) return { removedCount: 0, insertedText: '' };

  // Count Unicode code points rather than UTF-16 code units so an emoji emits
  // one DEL instead of two. This matches the character-oriented PTY contract.
  const oldCharacters = Array.from(oldValue);
  const newCharacters = Array.from(newValue);
  const minimumLength = Math.min(oldCharacters.length, newCharacters.length);

  let prefixLength = 0;
  while (
    prefixLength < minimumLength &&
    oldCharacters[prefixLength] === newCharacters[prefixLength]
  ) {
    prefixLength += 1;
  }

  return {
    removedCount: oldCharacters.length - prefixLength,
    insertedText: newCharacters.slice(prefixLength).join('')
  };
}

/** Android IME keyboards drive xterm's textarea through keyCode 229 flows. */
export function isAndroidImeUserAgent(userAgent: string): boolean {
  return /Android/i.test(userAgent);
}

type XtermCoreServiceLike = {
  triggerDataEvent: (data: string, wasUserInput: boolean) => void;
};

type XtermCompositionHelperLike = {
  _coreService?: XtermCoreServiceLike;
  _textarea?: HTMLTextAreaElement;
  _isComposing?: boolean;
  _isSendingComposition?: boolean;
  _dataAlreadySent?: string;
  _handleAnyTextareaChanges?: () => void;
};

type XtermInternalsLike = {
  _core?: {
    _compositionHelper?: XtermCompositionHelperLike | null;
  } | null;
};

type MinimalTextarea = Pick<HTMLTextAreaElement, 'value'> & {
  addEventListener?: HTMLTextAreaElement['addEventListener'];
  removeEventListener?: HTMLTextAreaElement['removeEventListener'];
};

/**
 * Installs the corrective diff on a terminal's composition helper.
 *
 * Returns a dispose function, or undefined when this device cannot reach the
 * required xterm internals (different xterm version, bundle minification
 * change) or the browser is not Android. Returning undefined keeps the panel
 * wiring trivially safe instead of degrading input.
 */
export function installTerminalAndroidImeDiff(terminal: Terminal): (() => void) | undefined {
  if (!isAndroidImeUserAgent(navigator.userAgent)) return undefined;

  // @xterm/xterm exports a public Terminal wrapper. CompositionHelper belongs
  // to its CoreBrowserTerminal, not to the public wrapper itself.
  const helper = (terminal as unknown as XtermInternalsLike)._core?._compositionHelper;
  const originalHandler = helper?._handleAnyTextareaChanges;
  if (!helper || typeof originalHandler !== 'function' || !helper._coreService) {
    return undefined;
  }
  const coreService = helper._coreService;

  let pendingSnapshot: string | null = null;
  let pendingFlush: ReturnType<typeof setTimeout> | null = null;
  let emittingCorrectiveDiff = false;

  const textarea = (): MinimalTextarea | undefined => helper._textarea ?? undefined;

  const cancelPendingDiff = () => {
    if (pendingFlush !== null) clearTimeout(pendingFlush);
    pendingFlush = null;
    pendingSnapshot = null;
  };

  // keyCode 229 and beforeinput/input are not mutually exclusive on Android.
  // If xterm independently delivers a key, paste, or backspace before this
  // shim's deferred diff runs, that input already owns the textarea change.
  // Discard the stale snapshot so a shortened textarea cannot emit a second
  // DEL (and an inserted character cannot be delivered twice).
  const dataDisposable = typeof terminal.onData === 'function'
    ? terminal.onData(() => {
      if (!emittingCorrectiveDiff) cancelPendingDiff();
    })
    : undefined;

  const flushPendingDiff = () => {
    pendingFlush = null;
    const box = textarea();
    const snapshot = pendingSnapshot;
    pendingSnapshot = null;
    if (!box || snapshot === null) return;

    // While a native composition owns the textarea, its own begin/update/end
    // flow sends text. Adopting the current value as the next baseline here
    // prevents the composition result from also being diffed against a stale
    // pre-composition snapshot.
    if (helper._isComposing || helper._isSendingComposition) {
      pendingSnapshot = box.value;
      return;
    }

    const diff = terminalTextDiff(snapshot, box.value);
    if (!diff.removedCount && !diff.insertedText) return;

    // Mirror xterm's bookkeeping so a following composition finalize subtracts
    // exactly what this diff already delivered.
    helper._dataAlreadySent = diff.insertedText;

    emittingCorrectiveDiff = true;
    try {
      for (let index = 0; index < diff.removedCount; index += 1) {
        coreService.triggerDataEvent('\u007f', true);
      }
      if (diff.insertedText) {
        coreService.triggerDataEvent(diff.insertedText, true);
      }
    } finally {
      emittingCorrectiveDiff = false;
    }
  };

  // xterm invokes this from its textarea keydown listener whenever Android
  // reports keyCode 229. The original implementation snapshots asynchronously
  // and diffs with String.replace; both halves misbehave on IME rewrites.
  helper._handleAnyTextareaChanges = () => {
    const box = textarea();
    if (!box) {
      originalHandler.call(helper);
      return;
    }
    // Keep the earliest snapshot of a burst: later 229 keydowns before the
    // flush describe intermediate IME states of the same edit.
    pendingSnapshot ??= box.value;
    if (pendingFlush !== null) return;
    pendingFlush = setTimeout(flushPendingDiff, 0);
  };

  const onCompositionEnd = () => {
    // xterm's finalizeComposition delivers the remaining composition text
    // from its own deferred task. Resync after that has run so the baseline
    // reflects what the PTY actually received.
    if (pendingFlush !== null) {
      clearTimeout(pendingFlush);
      pendingFlush = null;
    }
    setTimeout(() => {
      pendingSnapshot = textarea()?.value ?? null;
    }, 0);
  };
  textarea()?.addEventListener?.('compositionend', onCompositionEnd, true);

  return () => {
    cancelPendingDiff();
    dataDisposable?.dispose();
    textarea()?.removeEventListener?.('compositionend', onCompositionEnd, true);
    helper._handleAnyTextareaChanges = originalHandler;
  };
}
